import { DatabaseProvider } from "src/api/connectApi/types";
import {
  FeatureQueryConversationContextContextTypeEnum,
  NodeConversationContextContextTypeEnum,
} from "src/clients/copilot";
import { EventConfigEnriched } from "src/clients/features-control/api";
import {
  BeMappedNode,
  DatabaseConnectionNode,
} from "src/constants/NodeDataTypes";
import { NODE_TYPE } from "src/constants/NodeTypes";
import {
  RenderMentionFnArgs,
  renderSuggestion,
  renderTextContent,
} from "src/copilot/ConversationMessageItem";
import { NodeSuggestionType } from "src/copilot/NodeSuggestion";
import { QuerySuggestionType } from "src/copilot/QuerySuggestion";
import { EntitySchemaResource } from "src/entities/queries";

export const NODE_SUGGESTION_OPEN = "<node_suggestion>";
const NODE_SUGGESTION_CLOSE = "</node_suggestion>";

export const QUERY_SUGGESTION_OPEN = "<query_suggestion>";
const QUERY_SUGGESTION_CLOSE = "</query_suggestion>";

export const NODE_MENTION_OPEN = "<node_mention>";
export const NODE_MENTION_CLOSE = "</node_mention>";

export const ENTITY_EVENT_MENTION_OPEN = "<mentioned_resource>";
export const ENTITY_EVENT_MENTION_CLOSE = "</mentioned_resource>";

export const failureMessage =
  "I apologize, but I'm having trouble generating a valid response for this request. Despite multiple attempts, I couldn't find a solution that works within the current constraints. Our team has been notified of this issue and will investigate to improve future interactions. \n\n" +
  "To move forward, you could try:\n" +
  "1. Simplifying your request or breaking it down into smaller parts\n" +
  "2. Providing more context about what you're trying to achieve\n" +
  "3. Referencing other relevant nodes using @node_name to give me more information\n" +
  "4. Starting a new chat with a different approach \n\n" +
  "Thank you for your patience. I'm here to help if you'd like to try again with a new query.\n";

export const normalizeAnimationSpeed = (length: number) =>
  Math.min(0.0075 * length, 4);

export type NodeResource = {
  node: BeMappedNode;
  nodesArray: BeMappedNode[];
};

export type FeatureQueryResource = {
  entities: EntitySchemaResource[];
  events: EventConfigEnriched[];
};

export type ResourceType =
  | NodeConversationContextContextTypeEnum.NODE
  | FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY;

type ReplaceSuggestionsFnArgs = {
  baseKey: number;
  content: string;
  resourceData: NodeResource | FeatureQueryResource;
  resourceType: ResourceType;
  onApply: (suggestion: NodeSuggestionType | QuerySuggestionType) => void;
  animate: boolean;
  baseAnimationDelay: number;
  canEditFlowVersion: boolean;
};

type ReplaceMentionsFnArgs = {
  content: string;
  renderMention: (args: RenderMentionFnArgs) => React.ReactNode[];
  resourceType: ResourceType;
};

type SplitContentFnArgs = {
  content: string;
  openTag: string;
  closeTag: string;
};

const splitContent = ({ content, openTag, closeTag }: SplitContentFnArgs) => {
  const [tagOpenIndex, tagCloseIndex] = [
    content.indexOf(openTag),
    content.indexOf(closeTag),
  ];
  return [
    content.slice(0, tagOpenIndex),
    content.slice(tagOpenIndex + openTag.length, tagCloseIndex),
    content.slice(tagCloseIndex + closeTag.length),
  ];
};

export const getEntity = (
  resourceData: FeatureQueryResource,
  mention: string,
) => {
  return (
    resourceData.entities?.find(
      (e) => e._display_name_singular?.toLowerCase() === mention.toLowerCase(),
    ) ?? null
  );
};

export const getEvent = (
  resourceData: FeatureQueryResource,
  mention: string,
) => {
  return (
    resourceData.events?.find(
      (e) => e.display_name_singular?.toLowerCase() === mention.toLowerCase(),
    ) ?? null
  );
};

type ReplaceMentionsResult = {
  parts: React.ReactNode[];
  nextKey: number;
};

export const replaceMentions = ({
  content,
  renderMention,
  resourceType,
}: ReplaceMentionsFnArgs): ReplaceMentionsResult => {
  const getCurrentMention = () => {
    if (
      resourceType ===
      FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY
    ) {
      return [ENTITY_EVENT_MENTION_OPEN, ENTITY_EVENT_MENTION_CLOSE];
    }
    return [NODE_MENTION_OPEN, NODE_MENTION_CLOSE];
  };
  const [openTag, closeTag] = getCurrentMention();
  if (!content.includes(openTag)) {
    return { parts: [content], nextKey: 0 };
  }

  const parts: React.ReactNode[] = [];
  let key = 0;
  let contentToSearch = content;

  while (true) {
    const [textBlock, mention, rest] = splitContent({
      content: contentToSearch,
      openTag,
      closeTag,
    });

    // Don't trim - preserve whitespace for proper formatting
    if (textBlock) {
      parts.push(textBlock);
    }
    key++;

    parts.push(
      renderMention({
        mention,
        key,
        delay: normalizeAnimationSpeed(textBlock.length),
      }),
    );
    key++;

    if (rest.includes(openTag)) {
      contentToSearch = rest;
    } else {
      // Don't trim - preserve whitespace for proper formatting
      if (rest) {
        parts.push(rest);
      }
      break;
    }
  }

  return { parts, nextKey: key };
};

const getSuggestionsTags = (resourceType: ResourceType) => {
  if (
    resourceType ===
    FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY
  ) {
    return [QUERY_SUGGESTION_OPEN, QUERY_SUGGESTION_CLOSE];
  } else if (resourceType === NodeConversationContextContextTypeEnum.NODE) {
    return [NODE_SUGGESTION_OPEN, NODE_SUGGESTION_CLOSE];
  }
  return [];
};

export const replaceSuggestions = ({
  baseKey,
  content,
  resourceData,
  resourceType,
  onApply,
  animate,
  baseAnimationDelay,
  canEditFlowVersion,
}: ReplaceSuggestionsFnArgs): React.ReactNode[] => {
  const [openTag, closeTag] = getSuggestionsTags(resourceType);

  if (!content.includes(openTag)) {
    // No suggestions - render as inline text to flow naturally with mention pills
    return [
      renderTextContent({
        animate,
        content,
        delay: baseAnimationDelay,
        key: baseKey,
        inline: true,
      }),
    ];
  }

  const parts: React.ReactNode[] = [];
  let key = baseKey;
  let contentToSearch = content;
  let animationDelay = baseAnimationDelay;

  if (openTag && closeTag) {
    while (true) {
      const [textBlock, suggestion, rest] = splitContent({
        content: contentToSearch,
        openTag,
        closeTag,
      });

      if (textBlock) {
        parts.push(
          renderTextContent({
            key,
            animate,
            content: textBlock,
            delay: animationDelay,
            inline: true,
          }),
        );
      }

      animationDelay += normalizeAnimationSpeed(textBlock.length);
      key++;

      parts.push(
        renderSuggestion({
          suggestion,
          resourceData,
          resourceType,
          key,
          onApply,
          delay: animate ? animationDelay : 0,
          canEditFlowVersion,
        }),
      );
      key++;

      if (rest.includes(openTag)) {
        contentToSearch = rest;
      } else {
        if (rest) {
          parts.push(
            renderTextContent({
              key,
              animate,
              content: rest,
              delay: animationDelay,
              inline: true,
            }),
          );
        }
        break;
      }
    }
  }

  return parts;
};

const COPILOT_ENABLED_NODE_TYPES = [
  NODE_TYPE.CODE_NODE,
  NODE_TYPE.ASSIGNMENT_NODE,
  NODE_TYPE.RULE_NODE_V2,
  NODE_TYPE.SPLIT_NODE_V2,
  NODE_TYPE.ML_NODE,
  NODE_TYPE.SCORECARD_NODE,
];

export const COPILOT_ENABLED_DATABASES: DatabaseProvider[] = [
  "postgresql",
  "mysql",
  "snowflake",
  "redshift",
];

const isDatabaseConnectionNode = (
  node: BeMappedNode,
): node is DatabaseConnectionNode =>
  node.type === NODE_TYPE.SQL_DATABASE_CONNECTION_NODE;

export const getIsCopilotEnabled = (node: BeMappedNode): boolean => {
  if (COPILOT_ENABLED_NODE_TYPES.includes(node.type)) {
    return true;
  }

  if (isDatabaseConnectionNode(node)) {
    const provider = node.data.providerResource.provider;
    return COPILOT_ENABLED_DATABASES.includes(provider);
  }

  return false;
};
